import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Shield, AlertTriangle, Search, UserX, Eye, Ban, CheckCircle,
  FileText, TrendingUp, Lock, Flag, Activity
} from "lucide-react";

const SEVERITY_COLORS = { low: "bg-blue-100 text-blue-800", medium: "bg-amber-100 text-amber-800", high: "bg-red-100 text-red-800" };
const SCORE_COLORS = { none: "bg-green-100 text-green-800", monitor: "bg-amber-100 text-amber-800", freeze_rewards: "bg-orange-100 text-orange-800", suspend: "bg-red-100 text-red-800" };

export default function TrustSafety() {
  const [metrics, setMetrics] = useState(null);
  const [reports, setReports] = useState({ reports: [], counts: {} });
  const [repeatPairs, setRepeatPairs] = useState({ flagged_pairs: [], active_pairs: [] });
  const [kycUsers, setKycUsers] = useState([]);
  const [searchUserId, setSearchUserId] = useState("");
  const [userScore, setUserScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [metricsRes, reportsRes, pairsRes, kycRes] = await Promise.all([
        api.get("/trust/pilot-metrics"),
        api.get("/trust/reports"),
        api.get("/trust/repeat-pairs"),
        api.get("/trust/kyc-required"),
      ]);
      setMetrics(metricsRes.data);
      setReports(reportsRes.data);
      setRepeatPairs(pairsRes.data);
      setKycUsers(kycRes.data.users_needing_kyc || []);
    } catch (err) {
      toast.error("Failed to load trust data");
    } finally { setLoading(false); }
  };

  const lookupUser = async () => {
    if (!searchUserId.trim()) return;
    try {
      const res = await api.get(`/trust/score/${searchUserId}`);
      setUserScore(res.data);
    } catch { toast.error("User not found"); }
  };

  const recalculateScore = async (userId) => {
    try {
      const res = await api.post(`/trust/score/recalculate/${userId}`);
      setUserScore(res.data);
      toast.success("Score recalculated");
    } catch { toast.error("Failed to recalculate"); }
  };

  const resolveReport = async (reportId) => {
    try {
      await api.post(`/trust/reports/${reportId}/resolve`, { resolution: "Reviewed and resolved" });
      toast.success("Report resolved");
      fetchAll();
    } catch { toast.error("Failed to resolve"); }
  };

  if (loading) return <div className="flex justify-center items-center min-h-[400px]"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" /></div>;

  const fin = metrics?.financial || {};
  const fraud = metrics?.fraud || {};
  const ops = metrics?.operations || {};

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6" data-testid="trust-safety-page">
      <div className="flex items-center gap-3">
        <Shield className="w-6 h-6 text-primary" />
        <h1 className="font-heading text-2xl sm:text-3xl">Trust & Safety</h1>
        <Badge className={metrics?.health === "healthy" ? "bg-green-600 text-white" : metrics?.health === "warning" ? "bg-amber-500 text-white" : "bg-red-600 text-white"}>
          {metrics?.health || "unknown"}
        </Badge>
      </div>

      {/* Red Flags */}
      {metrics?.red_flags?.length > 0 && (
        <div className="space-y-2">
          {metrics.red_flags.map((f, i) => (
            <div key={i} className="p-3 rounded-lg bg-red-50 text-red-800 border border-red-200 flex items-center gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 shrink-0" /> {f}
            </div>
          ))}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-5 w-full">
          <TabsTrigger value="overview">Metrics</TabsTrigger>
          <TabsTrigger value="scoring">Fraud Scoring</TabsTrigger>
          <TabsTrigger value="incidents">Incidents ({reports.counts?.open || 0})</TabsTrigger>
          <TabsTrigger value="pairs">Repeat Pairs</TabsTrigger>
          <TabsTrigger value="kyc">KYC ({kycUsers.length})</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card><CardContent className="p-4 text-center">
              <TrendingUp className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold">₱{(fin.gmv || 0).toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">GMV</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center">
              <Lock className="w-5 h-5 text-blue-500 mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold">₱{(fin.commission_collected || 0).toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Commission</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center">
              <Activity className="w-5 h-5 text-amber-500 mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold">{fin.reward_payout_ratio || 0}%</p>
              <p className="text-xs text-muted-foreground">Reward/GMV Ratio</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center">
              <AlertTriangle className="w-5 h-5 text-red-500 mx-auto mb-1" />
              <p className="text-2xl font-heading font-bold">{ops.cancellation_rate || 0}%</p>
              <p className="text-xs text-muted-foreground">Cancel Rate</p>
            </CardContent></Card>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card><CardContent className="p-3 text-center">
              <p className="text-lg font-bold">{fraud.pending_flags || 0}</p>
              <p className="text-xs text-muted-foreground">Pending Fraud Flags</p>
            </CardContent></Card>
            <Card><CardContent className="p-3 text-center">
              <p className="text-lg font-bold">{fraud.high_risk_users || 0}</p>
              <p className="text-xs text-muted-foreground">High-Risk Users</p>
            </CardContent></Card>
            <Card><CardContent className="p-3 text-center">
              <p className="text-lg font-bold">{ops.open_incidents || 0}</p>
              <p className="text-xs text-muted-foreground">Open Incidents</p>
            </CardContent></Card>
            <Card><CardContent className="p-3 text-center">
              <p className="text-lg font-bold">{ops.total_bookings || 0}</p>
              <p className="text-xs text-muted-foreground">Total Bookings</p>
            </CardContent></Card>
          </div>
        </TabsContent>

        {/* Fraud Scoring */}
        <TabsContent value="scoring" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle className="font-heading text-base">User Fraud Score Lookup</CardTitle></CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Input value={searchUserId} onChange={e => setSearchUserId(e.target.value)} placeholder="Enter user_id..." data-testid="fraud-score-search" />
                <Button onClick={lookupUser} variant="outline"><Search className="w-4 h-4 mr-1" /> Lookup</Button>
              </div>
              {userScore && (
                <div className="space-y-4 border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{userScore.user_name}</p>
                      <p className="text-xs text-muted-foreground">{userScore.user_id}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-heading font-bold">{userScore.fraud?.fraud_score}</span>
                      <Badge className={SCORE_COLORS[userScore.fraud?.action] || "bg-gray-100"}>{userScore.fraud?.action}</Badge>
                    </div>
                  </div>
                  {/* Signal breakdown */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {Object.entries(userScore.fraud?.signals || {}).map(([key, val]) => (
                      <div key={key} className="bg-muted/50 rounded p-2">
                        <p className="text-xs font-medium capitalize">{key.replace(/_/g, " ")}</p>
                        <p className="text-lg font-bold">{val.score}</p>
                      </div>
                    ))}
                  </div>
                  {/* Reliability */}
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div>
                      <p className="text-sm">Reliability: <span className="font-bold">{userScore.reliability?.reliability_score}</span>/100</p>
                      <p className="text-xs text-muted-foreground">Badge: {userScore.reliability?.badge} | Cancel: {userScore.reliability?.cancellation_rate}%</p>
                    </div>
                    <Button size="sm" onClick={() => recalculateScore(userScore.user_id)} data-testid="recalculate-btn">Recalculate</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Incidents */}
        <TabsContent value="incidents" className="space-y-4 mt-4">
          <div className="flex gap-2 mb-3">
            {Object.entries(reports.counts || {}).map(([k, v]) => (
              <Badge key={k} variant={k === "open" ? "destructive" : "secondary"}>{k}: {v}</Badge>
            ))}
          </div>
          {(reports.reports || []).length === 0 ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">No incident reports</CardContent></Card>
          ) : (
            <div className="space-y-3">
              {reports.reports.map(r => (
                <Card key={r.report_id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={SEVERITY_COLORS[r.incident_severity] || "bg-gray-100"}>{r.incident_severity}</Badge>
                          <Badge variant="outline">{r.incident_type}</Badge>
                          <Badge variant={r.status === "open" ? "destructive" : "secondary"}>{r.status}</Badge>
                        </div>
                        <p className="text-sm">Reported by: {r.reporter_name}</p>
                        <p className="text-xs text-muted-foreground mt-1">{r.description}</p>
                        <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</p>
                      </div>
                      {r.status === "open" && (
                        <Button size="sm" variant="outline" onClick={() => resolveReport(r.report_id)}>
                          <CheckCircle className="w-3 h-3 mr-1" /> Resolve
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Repeat Pairs */}
        <TabsContent value="pairs" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><Flag className="w-4 h-4 text-red-500" /> Flagged Pairs (Dropped Off)</CardTitle></CardHeader>
            <CardContent>
              {(repeatPairs.flagged_pairs || []).length === 0 ? (
                <p className="text-muted-foreground text-center py-4 text-sm">No flagged pairs detected</p>
              ) : (
                <div className="space-y-2">
                  {repeatPairs.flagged_pairs.map((p, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div>
                        <p className="text-sm font-medium">{p.user_name} → {p.venue_name}</p>
                        <p className="text-xs text-muted-foreground">{p.total_bookings} bookings, last: {p.last_booking?.slice(0, 10)}</p>
                      </div>
                      <Badge variant="destructive">dropped off</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* KYC */}
        <TabsContent value="kyc" className="space-y-4 mt-4">
          {kycUsers.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">No users require KYC</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {kycUsers.map((u, i) => (
                <Card key={i}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{u.name}</p>
                      <p className="text-xs text-muted-foreground">Trigger: {u.trigger} | KYC: {u.kyc_status || "none"}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={async () => {
                      await api.post(`/trust/kyc/${u.user_id}`, { status: "verified" });
                      toast.success("KYC verified");
                      fetchAll();
                    }}>Verify KYC</Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
